import React, {useCallback, useEffect, useState, createContext, useContext} from "react";
import {useLocalStorage} from "../hooks";
import {applyRenewedToken, getExpirationTime, isValidSession, normalizeSession} from "../utils/session";
import {environment} from "../../config/environment";

const AuthContext = createContext(null);
const demoUser = {
    username: 'demo',
    email: 'demo@busifarm.com.br',
    accessToken: 'demo-token',
};
const STORAGE_KEY = 'userLogged';

const readStoredSession = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
        return null;
    }
};

export function AuthProvider({ children }) {
    const [userLogged, setUserLogged] = useLocalStorage('userLogged', null);
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(environment.disableAuth);
    const validAuthenticated = environment.disableAuth || (authenticated && isValidSession(userLogged));

    useEffect(() => {
        if (environment.disableAuth) {
            setUserLogged(demoUser);
            setAuthenticated(true);
            setLoading(false);
            return;
        }

        const userRecovered = localStorage.getItem('userLogged');
        if (userRecovered) {
            try {
                const parsedUser = normalizeSession(JSON.parse(userRecovered));
                if (isValidSession(parsedUser)) {
                    setUserLogged(parsedUser);
                    setAuthenticated(true);
                } else {
                    setUserLogged(null);
                    setAuthenticated(false);
                }
            } catch {
                setUserLogged(null);
                setAuthenticated(false);
            }
        } else {
            setAuthenticated(false);
        }
        setLoading(false);
    }, [setUserLogged]);

    const login = useCallback((user) => {
        if (environment.disableAuth) {
            setUserLogged(demoUser);
            setAuthenticated(true);
            return;
        }

        const session = normalizeSession(user);
        if (isValidSession(session)) {
            setUserLogged(session);
            setAuthenticated(true);
        } else {
            setUserLogged(null);
            setAuthenticated(false);
        }
    }, [setUserLogged]);

    /**
     * Grava o token renovado (ver `VigiaDeSessao`). Parte da sessão guardada, que é a que as abas
     * dividem, e grava já no storage: o interceptor da API lê de lá, e as outras abas acompanham
     * pelo evento `storage`. Devolve nulo quando a resposta não serve, e nada muda.
     */
    const renovarToken = useCallback((accessToken, expiresIn) => {
        const renovada = applyRenewedToken(readStoredSession(), accessToken, expiresIn);
        if (!renovada) return null;

        localStorage.setItem(STORAGE_KEY, JSON.stringify(renovada));
        setUserLogged(renovada);
        setAuthenticated(true);
        return renovada;
    }, [setUserLogged]);

    // A sessão fica no localStorage, que as abas dividem. Sem acompanhar as outras abas, a que
    // ficou parada seguiria com o token antigo e, ao vencer, apagaria a sessão que a aba em uso
    // acabou de renovar. Sair numa aba também sai nas outras.
    useEffect(() => {
        if (environment.disableAuth) return undefined;

        const aoMudarEmOutraAba = (event) => {
            if (event.key !== STORAGE_KEY) return;
            let outra = null;
            try {
                outra = event.newValue ? normalizeSession(JSON.parse(event.newValue)) : null;
            } catch {
                return;
            }
            if (isValidSession(outra)) {
                setUserLogged(outra);
                setAuthenticated(true);
            } else {
                setUserLogged(null);
                setAuthenticated(false);
            }
        };

        window.addEventListener('storage', aoMudarEmOutraAba);
        return () => window.removeEventListener('storage', aoMudarEmOutraAba);
    }, [setUserLogged]);

    const logout = useCallback(() => {
        if (environment.disableAuth) {
            setUserLogged(demoUser);
            setAuthenticated(true);
            return;
        }

        setUserLogged(null);
        setAuthenticated(false);
    }, [setUserLogged]);

    useEffect(() => {
        if (environment.disableAuth) return undefined;
        if (!authenticated || !userLogged) return undefined;

        const expirationTime = getExpirationTime(userLogged);
        if (!expirationTime || expirationTime <= Date.now()) {
            logout();
            return undefined;
        }

        const timeoutId = window.setTimeout(logout, expirationTime - Date.now());
        return () => window.clearTimeout(timeoutId);
    }, [authenticated, logout, userLogged]);

    return (
        <AuthContext.Provider
            value={{
                userLogged,
                login,
                logout,
                renovarToken,
                authenticated: validAuthenticated,
                loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);
